import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import './ReportProblem.css';

const CATEGORIES = [
  { id: 'pollution', icon: '🏭', title: 'Pollution / Dumping', desc: 'Oil spills, plastic waste, chemical dumping, sewage discharge', color: '#ef4444', contacts: ['Coast Guard: 1554', 'Pollution Control Board: 1800-180-4999', 'Local Municipal Corp'] },
  { id: 'animal', icon: '🐢', title: 'Stranded / Injured Animal', desc: 'Dolphins, turtles, whales, seals, seabirds found stranded or hurt', color: '#f59e0b', contacts: ['Wildlife SOS: 1800-599-4442', 'SPCA / Blue Cross', 'Forest Dept: 1800-425-1515'], steps: ['Do NOT push the animal back into water', 'Keep people & pets away (minimum 15 meters)', 'Note the exact location & take photos', 'Call Wildlife SOS immediately: 1800-599-4442', 'Keep the animal moist with wet cloth (not eyes/blowhole)', 'Wait for rescue team — do not attempt to treat'] },
  { id: 'fishing', icon: '🎣', title: 'Illegal Fishing', desc: 'Dynamite fishing, cyanide use, trawling in protected zones, undersized catch', color: '#a855f7', contacts: ['Marine Police: 100', 'Coast Guard: 1554', 'Fisheries Dept Helpline'] },
  { id: 'coral', icon: '🪸', title: 'Coral / Reef Damage', desc: 'Anchor damage, bleaching events, reef destruction, sand mining', color: '#ec4899', contacts: ['Coast Guard: 1554', 'National Centre for Sustainable Coastal Management', 'MoEFCC: 011-24695386'] },
  { id: 'flood', icon: '🌊', title: 'Flood / Storm Emergency', desc: 'Coastal flooding, storm surge, tsunami warning, erosion emergency', color: '#3b82f6', contacts: ['NDMA Helpline: 1078', 'Coast Guard: 1554', 'State Disaster Management: 112'] },
  { id: 'other', icon: '📋', title: 'Other Issue', desc: 'Beach safety hazard, navigation danger, water quality concern, other marine issues', color: '#0097b2', contacts: ['Coast Guard: 1554', 'Local Police: 100', 'District Collector Office'] },
];

const ReportProblem = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', location: '', description: '', urgency: 'medium' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [showReports, setShowReports] = useState(false);

  const auth = getAuth();

  // Fetch recent reports on mount
  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRecentReports(reports);
    } catch (err) {
      console.log('Error fetching reports:', err);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to Firebase Firestore
      await addDoc(collection(db, 'reports'), {
        category: selectedCategory,
        categoryTitle: category.title,
        name: formData.name,
        location: formData.location,
        description: formData.description,
        urgency: formData.urgency,
        userEmail: auth.currentUser?.email || 'anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
      });

      setSubmitted(true);
      showToast('✅ Report submitted successfully!');
      fetchReports(); // Refresh the list
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error saving report:', error);
      showToast('❌ Failed to save report. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const category = CATEGORIES.find(c => c.id === selectedCategory);

  const getUrgencyBadge = (urgency) => {
    const map = { low: '🟢 Low', medium: '🟡 Medium', high: '🔴 High' };
    return map[urgency] || urgency;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="report-page">
      {/* Toast */}
      {toast && (
        <div className={`report-toast ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="report-container">
        <div className="report-header">
          <span className="report-badge">🚨 Get Help</span>
          <h1>Report a Problem</h1>
          <p className="report-subtitle">Select the type of issue you're facing. We'll guide you on what to do and who to contact.</p>
        </div>

        {submitted ? (
          <div className="report-success">
            <div className="success-icon">✅</div>
            <h2>Report Submitted Successfully</h2>
            <p>Your report has been recorded. Our team and relevant authorities have been notified and will take action soon.</p>
            <div className="success-actions">
              <h3>🚨 Immediate Actions</h3>
              <p>If this is an emergency, please also contact these numbers directly:</p>
              <div className="emergency-numbers-inline">
                {category?.contacts.map((c, i) => (
                  <a key={i} href={`tel:${c.split(':')[1]?.trim()}`} className="emergency-call-btn">
                    📞 {c}
                  </a>
                ))}
              </div>
            </div>
            <button className="report-new-btn" onClick={() => { setSubmitted(false); setSelectedCategory(null); setFormData({ name: '', location: '', description: '', urgency: 'medium' }); }}>
              Report Another Issue
            </button>
          </div>
        ) : !selectedCategory ? (
          <>
            <div className="category-grid">
              {CATEGORIES.map(cat => (
                <div 
                  key={cat.id} 
                  className="category-card" 
                  style={{ '--cat-color': cat.color }}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <div className="category-icon">{cat.icon}</div>
                  <h3>{cat.title}</h3>
                  <p>{cat.desc}</p>
                  <span className="category-arrow">→</span>
                </div>
              ))}
            </div>

            <div className="emergency-banner">
              <h3>🆘 In Immediate Danger?</h3>
              <p>If you or someone else is in immediate danger, call emergency services right away.</p>
              <div className="emergency-buttons">
                <a href="tel:112" className="emergency-btn primary-emergency">📞 Emergency: 112</a>
                <a href="tel:1554" className="emergency-btn">🚢 Coast Guard: 1554</a>
                <a href="tel:1078" className="emergency-btn">🌊 NDMA: 1078</a>
              </div>
            </div>

            {/* Recent Reports Section */}
            <div className="recent-reports-section">
              <button className="toggle-reports-btn" onClick={() => setShowReports(!showReports)}>
                {showReports ? '▾' : '▸'} Recent Reports ({recentReports.length})
              </button>
              
              {showReports && (
                <div className="reports-list">
                  {recentReports.length === 0 ? (
                    <p className="no-reports">No reports yet. Be the first to report an issue!</p>
                  ) : (
                    recentReports.slice(0, 10).map((report) => (
                      <div key={report.id} className="report-item">
                        <div className="report-item-header">
                          <span className="report-item-category">
                            {CATEGORIES.find(c => c.id === report.category)?.icon} {report.categoryTitle}
                          </span>
                          <span className="report-item-urgency">{getUrgencyBadge(report.urgency)}</span>
                        </div>
                        <p className="report-item-desc">{report.description?.substring(0, 120)}{report.description?.length > 120 ? '...' : ''}</p>
                        <div className="report-item-meta">
                          <span>📍 {report.location}</span>
                          <span>🕐 {formatDate(report.createdAt)}</span>
                          <span className={`status-badge status-${report.status}`}>{report.status}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="report-form-section">
            <button className="back-btn" onClick={() => setSelectedCategory(null)}>← Back to categories</button>

            <div className="selected-category-header" style={{ '--cat-color': category.color }}>
              <span className="selected-icon">{category.icon}</span>
              <div>
                <h2>{category.title}</h2>
                <p>{category.desc}</p>
              </div>
            </div>

            {/* Rescue Steps (for stranded animals) */}
            {category.steps && (
              <div className="rescue-steps">
                <h3>🏥 Immediate Steps</h3>
                <ol>
                  {category.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Emergency Contacts for this category */}
            <div className="category-contacts">
              <h3>📞 Contact These Authorities</h3>
              <div className="contact-list">
                {category.contacts.map((contact, i) => (
                  <a key={i} href={`tel:${contact.split(':')[1]?.trim()}`} className="contact-card">
                    <span className="contact-icon">📞</span>
                    <span>{contact}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Report Form */}
            <form className="report-form" onSubmit={handleSubmit}>
              <h3>📝 Submit a Detailed Report</h3>
              <p className="form-note">Your report will be recorded for tracking and follow-up.</p>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Your Name</label>
                  <input type="text" placeholder="Full name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Urgency Level</label>
                  <select value={formData.urgency} onChange={e => setFormData({...formData, urgency: e.target.value})}>
                    <option value="low">🟢 Low — Not urgent</option>
                    <option value="medium">🟡 Medium — Needs attention</option>
                    <option value="high">🔴 High — Emergency</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Location</label>
                <input type="text" placeholder="e.g., Marina Beach, Chennai, Tamil Nadu" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} required />
              </div>

              <div className="form-group">
                <label>Describe the Problem</label>
                <textarea placeholder="Provide as much detail as possible — what you see, when it started, how severe it is..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} required rows="5"></textarea>
              </div>

              <button type="submit" className="submit-report-btn" disabled={isSubmitting}>
                {isSubmitting ? '⏳ Submitting...' : '🚀 Submit Report'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportProblem;
